/**
 * 실카드 아트 매핑 — public/images/cards/{agentId}/{finish}.webp(480w) +
 * {finish}@2x.webp(960w), 5:7 세로. 실존 에셋 조합만 상수 테이블로 판정한다
 * (fetch 검사 금지) — 미존재 조합은 null 반환, 호출부는 기존 폴백(초상/전신/
 * 자리표시)을 유지한다.
 */

const SAPPHIRE_TRIO = ["blue_ice_sapphire", "gold_ice_sapphire", "red_ice_sapphire"];

/** agentId → 실존 아트 finish 목록. openai-gpt만 base·gold_vinyl 추가 보유. */
const AGENT_FINISHES: Record<string, string[]> = {
  "openai-gpt": [...SAPPHIRE_TRIO, "base", "gold_vinyl"],
  "grok-x": SAPPHIRE_TRIO,
  "qwen-3-6-plus": SAPPHIRE_TRIO,
  "gemini-pro": SAPPHIRE_TRIO,
  "deepseek-chat": SAPPHIRE_TRIO,
  "claude-opus": SAPPHIRE_TRIO,
};

/**
 * Holo 피니시는 전용 아트가 없다 — Base 아트 위에 rainbow-holo 오버레이를 얹는
 * 구성(pokemon-cards-css 기반, PioneerHoloCard effect 또는 .ty-foil)이므로
 * 아트 경로는 base를 재사용한다.
 */
function artFinish(finish: string): string {
  return finish === "holo" ? "base" : finish;
}

/** 480w(1x) 아트 경로 — 미존재 조합은 null. */
export function cardArtSrc(agentId: string, finish: string): string | null {
  const resolved = artFinish(finish);
  return AGENT_FINISHES[agentId]?.includes(resolved)
    ? `/images/cards/${agentId}/${resolved}.webp`
    : null;
}

/** 960w(@2x) 아트 경로 — 확대 뷰(팝오버·카드 상세)용. */
export function cardArt2xSrc(agentId: string, finish: string): string | null {
  return cardArtSrc(agentId, finish)?.replace(/\.webp$/, "@2x.webp") ?? null;
}

/** `<img srcSet>`용 1x/2x 셋. */
export function cardArtSrcSet(agentId: string, finish: string): string | null {
  const src = cardArtSrc(agentId, finish);
  return src ? `${src} 1x, ${src.replace(/\.webp$/, "@2x.webp")} 2x` : null;
}

/**
 * 시리얼별 개별 아트 — public/images/cards/{agentId}/{finish}/{serial}.webp(480w).
 * 원본 966장이 시리얼 단위(1..발행량)로 존재. 범위는 finish 공급량과 동일:
 * blue_ice 1..99 · gold_ice 1..50 · red_ice 1..10 (6비서 전원),
 * openai-gpt 한정 gold_vinyl/base 1. 범위 밖·미존재 조합은 null.
 */
const SERIAL_COUNTS: Record<string, number> = {
  blue_ice_sapphire: 99,
  gold_ice_sapphire: 50,
  red_ice_sapphire: 10,
};

export function cardSerialArtSrc(
  agentId: string,
  finish: string,
  serial: number | null | undefined,
): string | null {
  if (serial == null || !Number.isInteger(serial) || serial < 1) return null;
  if (!AGENT_FINISHES[agentId]?.includes(finish)) return null;
  const max = agentId === "openai-gpt" && (finish === "gold_vinyl" || finish === "base")
    ? 1
    : SERIAL_COUNTS[finish] ?? 0;
  if (serial > max) return null;
  return `/images/cards/${agentId}/${finish}/${serial}.webp`;
}
